/**
 * 参加済みグループとトークンの端末内保存（仕様書 §4.1 G-03 / G-05）。
 *
 * アカウントがないため、ここが唯一の「自分が入っているグループ」の記録になる。
 * localStorage が使えない環境（プライベートブラウズ、設定でサイトデータ拒否）
 * でもアプリ自体は動くべきなので、読み書きはすべて例外を飲み込む。
 */

const STORAGE_KEY = 'mahjong-score:groups';

export interface StoredGroup {
  groupId: string;
  /** 平文トークン。共有URLのフラグメントから取得したもの */
  token: string;
  /** 一覧表示用のキャッシュ。開くたびに最新化する */
  name: string;
  playerCount?: 3 | 4;
  matchCount?: number;
  /** 最後の対局日時。カードの「最終記録 8/31」に使う */
  lastMatchAt?: string;
  lastOpenedAt: string;
}

function readAll(): StoredGroup[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (g): g is StoredGroup =>
        typeof g === 'object' &&
        g !== null &&
        typeof (g as StoredGroup).groupId === 'string' &&
        typeof (g as StoredGroup).token === 'string',
    );
  } catch {
    return [];
  }
}

function writeAll(groups: StoredGroup[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
  } catch {
    // 容量超過や保存拒否。記憶できないだけで操作は続行できる
  }
}

/** 最近開いた順のグループ一覧 */
export function listStoredGroups(): StoredGroup[] {
  return readAll().sort((a, b) => (a.lastOpenedAt < b.lastOpenedAt ? 1 : -1));
}

export function getStoredGroup(groupId: string): StoredGroup | null {
  return readAll().find((g) => g.groupId === groupId) ?? null;
}

export function getStoredToken(groupId: string): string | null {
  return getStoredGroup(groupId)?.token ?? null;
}

/** 既存があれば上書きマージ、なければ追加する */
export function upsertStoredGroup(
  group: Pick<StoredGroup, 'groupId' | 'token'> & Partial<StoredGroup>,
): void {
  const all = readAll();
  const i = all.findIndex((g) => g.groupId === group.groupId);
  const merged: StoredGroup = {
    ...(i >= 0 ? all[i]! : {}),
    ...group,
    lastOpenedAt: group.lastOpenedAt ?? new Date().toISOString(),
    name: group.name ?? (i >= 0 ? all[i]!.name : ''),
  };

  if (i >= 0) {
    all[i] = merged;
  } else {
    all.push(merged);
  }
  writeAll(all);
}

export function removeStoredGroup(groupId: string): void {
  writeAll(readAll().filter((g) => g.groupId !== groupId));
}

/**
 * 共有URL `#t={token}` からトークンを読み取る（§4.1 G-02）。
 *
 * フラグメントはブラウザがサーバーへ送らないため、CloudFront にも API Gateway
 * にもトークンは到達しない。
 *
 * 読み取りと消去を分けているのは StrictMode 対策。useState の初期化関数は開発時に
 * 2回呼ばれるので、ここで履歴を書き換えてしまうと2回目に null が返り、取得した
 * はずのトークンを取りこぼす。この関数は副作用を持たず何度呼んでも同じ値を返す。
 */
export function readTokenFromHash(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#')) return null;
  return new URLSearchParams(hash.slice(1)).get('t');
}

/**
 * URLフラグメントからトークンを消す。保存が済んだあとに1度だけ呼ぶ。
 * 画面のスクリーンショットや、URLバーからの意図しない再共有を減らす。
 */
export function clearTokenFromHash(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    // 履歴を書き換えられなくても、トークンは保存済みなので動作に影響はない
  }
}

/** 共有URL全体を組み立てる（§4.1 G-02） */
export function buildShareUrl(groupId: string, token: string): string {
  return `${window.location.origin}/g/${groupId}#t=${token}`;
}
